import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { MultipleSubImage } from '../../../components/SubImage';
import { TrackedButton as Button } from '../../../components/TrackedComponents';
import imageNA from '../../../images/image_not_available.png';
import { VideoStreamType } from '../../../utils/ImageDimensions';
import { SyntheticCoachSession } from './Defs';

const STEPS = [
    { key: 1, label: 'Review or edit session' },
    { key: 2, label: 'Send to driver' },
    { key: 3, label: 'Get driver response' },
];

function getCurrentStep(session: SyntheticCoachSession): number {
    if (session.done) return 4;
    if (session.sent) return 3;
    if (session.is_saved) return 2;
    return 1;
}

function getIncidentsSummary(session: SyntheticCoachSession): string {
    const count = session.events?.length ?? 0;
    if (count === 0) return 'No incidents';
    const timestamps = (session.events ?? [])
        .map((e) => e.event_details?.timestamp)
        .filter((t): t is number => t != null);
    if (timestamps.length === 0) {
        return `${count} incident${count !== 1 ? 's' : ''}`;
    }
    const min = Math.min(...timestamps);
    const max = Math.max(...timestamps);
    const from = dayjs.unix(min).format('MMM D');
    const to = dayjs.unix(max).format('MMM D');
    return `${count} incident${count !== 1 ? 's' : ''} between ${from}–${to}`;
}

const StepDivider = () => (
    <Box sx={{ mt: '14px', width: '100%', height: 2, backgroundColor: 'grey.700', alignSelf: 'flex-start' }} />
);

interface CoachingSessionComponentProps {
    session: SyntheticCoachSession;
    sessionImage?: string | null;
}

const CoachingSessionComponent: React.FC<CoachingSessionComponentProps> = ({ session, sessionImage }) => {
    const history = useHistory();
    const currentStep = getCurrentStep(session);
    const incidentsSummary = getIncidentsSummary(session);
    const driverLine = [session.license_plate, session.driver_name].filter(Boolean).join(' / ') || '—';
    const imageSrc = sessionImage && sessionImage !== imageNA ? sessionImage : imageNA;

    const imageContainerStyle: React.CSSProperties = {
        height: '160px',
        width: '213px',
        minWidth: '213px',
        overflow: 'hidden',
        borderRadius: '8px',
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', py: 2 }}>
            <Box sx={{ position: 'relative', m: 2, ...imageContainerStyle }}>
                <MultipleSubImage
                    id={`coaching-session-${session.session_id}-image`}
                    image={imageSrc}
                    streams={[VideoStreamType.InCabinCamera]}
                    containerStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
            </Box>
            <Box
                sx={{
                    flex: 1,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {driverLine}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {incidentsSummary}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, my: 1.5 }}>
                    {STEPS.map((step, idx) => {
                        const done = currentStep > step.key;
                        const active = currentStep === step.key;
                        return (
                            <React.Fragment key={step.key}>
                                {idx > 0 && <StepDivider />}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            backgroundColor: 'grey.800',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {done ? <CheckIcon sx={{ fontSize: '16px' }} /> : step.key}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            mt: 0.5,
                                            textAlign: 'center',
                                            maxWidth: 72,
                                            color: active || done ? 'text.primary' : 'text.secondary',
                                        }}
                                    >
                                        {step.label}
                                    </Typography>
                                </Box>
                            </React.Fragment>
                        );
                    })}
                </Box>
                <Button
                    id={`view-coaching-session-${session.session_id}`}
                    variant="contained"
                    color="secondary"
                    onClick={() => history.push(`/coaching/${session.session_id}`)}
                    sx={{ width: '60%' }}
                >
                    View coaching session
                </Button>
            </Box>
        </Box>
    );
};

export default CoachingSessionComponent;
